import { useRef, useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router'
import {
  CogIcon,
  ArrowRightStartOnRectangleIcon,
} from '@heroicons/react/24/outline'
import { clsx } from 'clsx'

interface UserDropdownProps {
  user: {
    name?: string
    username?: string
    authProvider?: string
  } | null
  collapsed: boolean
  onLogout: () => void
}

// Delay before closing the dropdown when mouse leaves (prevents accidental closure)
const CLOSE_DELAY_MS = 150

export function UserDropdown({ user, collapsed, onLogout }: UserDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const navigate = useNavigate()

  const getUserInitial = () => {
    if (user?.authProvider && user.authProvider !== 'local') {
      return user.name && user.name.length > 0 ? user.name.charAt(0).toUpperCase() : 'U'
    }
    return user?.username && user.username.length > 0 ? user.username.charAt(0).toUpperCase() : 'U'
  }

  const getUserDisplayName = () => {
    if (user?.authProvider && user.authProvider !== 'local') {
      return user.name || 'User'
    }
    return user?.username || 'User'
  }

  const handleToggle = () => {
    setIsOpen(prev => !prev)
  }

  // Clear any pending close timeout
  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
  }, [])

  // Open dropdown on mouse enter
  const handleMouseEnter = useCallback(() => {
    clearCloseTimeout()
    setIsOpen(true)
  }, [clearCloseTimeout])

  // Close dropdown with delay on mouse leave
  const handleMouseLeave = useCallback(() => {
    clearCloseTimeout()
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false)
    }, CLOSE_DELAY_MS)
  }, [clearCloseTimeout])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      clearCloseTimeout()
    }
  }, [clearCloseTimeout])

  const handleSettingsClick = () => {
    navigate('/settings')
    setIsOpen(false)
  }

  const handleLogoutClick = () => {
    setIsOpen(false)
    onLogout()
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const { target } = event
      if (dropdownRef.current && target instanceof Node && !dropdownRef.current.contains(target)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Close dropdown on Escape key
  useEffect(() => {
    function handleEscapeKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey)
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [isOpen])

  return (
    <div
      ref={dropdownRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* User Avatar Button */}
      <button
        type="button"
        onClick={handleToggle}
        data-testid="user-dropdown-trigger"
        aria-expanded={isOpen}
        aria-haspopup="true"
        className={clsx(
          'flex items-center rounded-lg transition-colors',
          collapsed
            ? 'w-full justify-center p-2 hover:bg-gray-100 dark:hover:bg-gray-700'
            : 'w-full px-2 py-2 hover:bg-gray-100 dark:hover:bg-gray-700',
          isOpen && 'bg-gray-100 dark:bg-gray-700'
        )}
      >
        <div className={clsx(
          'bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0',
          collapsed ? 'w-6 h-6' : 'w-8 h-8'
        )}>
          <span className={clsx(
            'font-medium text-primary-700',
            collapsed ? 'text-xs' : 'text-sm'
          )}>
            {getUserInitial()}
          </span>
        </div>
        {!collapsed && (
          <div className="flex-1 min-w-0 ml-3 text-left">
            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {getUserDisplayName()}
            </p>
          </div>
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          data-testid="user-dropdown-menu"
          className={clsx(
            'absolute bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50',
            collapsed
              ? 'left-full bottom-0 ml-2 w-48'
              : 'left-0 bottom-full mb-2 w-full'
          )}
          role="menu"
          aria-orientation="vertical"
        >
          <div className="py-1">
            <button
              type="button"
              onClick={handleSettingsClick}
              data-testid="user-dropdown-settings"
              className="w-full flex items-center px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              role="menuitem"
            >
              <CogIcon className="w-5 h-5 text-gray-500 dark:text-gray-400 mr-3" />
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                Settings
              </span>
            </button>
            <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
            <button
              type="button"
              onClick={handleLogoutClick}
              data-testid="user-dropdown-logout"
              className="w-full flex items-center px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-red-600 dark:text-red-400"
              role="menuitem"
            >
              <ArrowRightStartOnRectangleIcon className="w-5 h-5 mr-3" />
              <span className="text-sm font-medium">
                Logout
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
